<template>
  <div>
    <header-banner title="State Pension help" sub-title="Help with your State Pension in your Plan"></header-banner>

    <div v-if="showOverSPA" class="row">
      <div class="col-md-8 mx-auto">
        <p>
          You are over your State Pension Age.
        </p>
        <p>
          Your State Pension will already be in payment and you will know what
          you are being paid. Just use the current annual figure in your plan.
        </p>
        <p>
          This will increase at the maximum of 2.5%, inflation (CPI) and average
          earnings each April. We assume the future increase will be 2.5% a
          year, which is the most cautious, but will update for actual increases
          automatically when known.
        </p>
      </div>
    </div>

    <div v-if="showUnderSPAIntro" class="row">
      <div class="col-md-8 mx-auto">
        <p>
          You are under State Pension Age.
        </p>
        <p>
          We have currently estimated you will get a full flat rate revalued
          State Pension of
          <span class="fw-bold">{{ estimatedStatePension }}</span> from your
          State Pension Age.
        </p>
        <p>
          However, as ever, things are a bit more complex than that.
        </p>
        <p>
          Your actual State Pension depends on the level of National Insurance
          (NI) contributions you will have at State Pension Age. OK here is the
          good news, you can
          <a
            href="https://www.gov.uk/check-national-insurance-record"
            target="_blank"
            >get an estimate of your NI contribution history to the current
            date</a
          >. Once there, you need to set up a Government Gateway login if you
          don't have one, details of how to do that are on the page.
        </p>
        <p>
          Once you are set up and have logged in, click check National Insurance
          contribution and you will see
        </p>
        <ul>
          <li>X years of full contributions</li>
          <li>
            Y years to contribute before the {{ statePensionEntitlementDate }}
          </li>
        </ul>
        <div class="row">
          <div class="col-5">
            <span class="me-2">Enter the figure for X here:</span>
            <input
              type="text"
              v-model="yearsFullContribsModel"
              size="3"
              maxlength="2"
              @keypress="checkNumeric"
            />
          </div>
        </div>
        <p class="mt-3">
          As shown you still have Y more years before your State Pension Age to
          gain more NI contributions.
        </p>
        <p>
          How many of those remaining Y years do you think you will be working
          (or claiming benefits of some kind, child, unemployment, carer
          benefits as examples)?
        </p>
        <div class="row">
          <div class="col">
            <span class="me-2"
              >Enter the number of those remaining years here:</span
            >
            <input
              type="text"
              v-model="yearsFutureContribsModel"
              size="3"
              maxlength="2"
              @keypress="checkNumeric"
            />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <button
              type="button"
              class="btn btn-primary"
              @click="onUnderSPAIntroNextClick"
            >
              Next
            </button>
          </div>
        </div>
        <div v-if="showUnderSPAError" class="row mt-3">
          <div class="col">
            <span class="warn"
              >Please enter the figures for contribution years.</span
            >
          </div>
        </div>
      </div>
    </div>

    <div v-if="showInitialEst" class="row">
      <div class="col-md-8 mx-auto">
        <p class="mt-3">
          You have told us by the time you reach State Pension Age you expect to
          have paid a total of
          <span class="fw-bold">{{ totalContributionYears }}</span>
          years of NI contributions.
        </p>
        <p>
          <template v-if="revisedStatePension == 0">
            Given this, we believe you may not receive a State Pension as you
            expect to have fewer than 10 years of contributions.
          </template>
          <template v-else>
            Given this, as an estimate, we believe you may receive a revalued
            State Pension of
            <span class="fw-bold">{{ revisedStatePensionFormatted }}</span>
            from State Pension Age.
          </template>
        </p>

        <p>
          <a
            href="https://www.moneysavingexpert.com/savings/voluntary-national-insurance-contributions/"
            target="_blank"
            >{{ entitlementAdvice }}</a
          >
        </p>

        <div v-if="hasQualifiedForPension" class="row">
          <div class="col">
            <p>
              The above is perfectly fine as an estimate, but if you want to
              refine this, the main further adjustment is that for many people,
              some of their previous NI years are in respect of "contracted out
              NI years" which can reduce the estimate a bit.
            </p>
            <options-question
              name="refineContractedOutQuestion"
              question="Do you want to consider contracted out years further?"
              :options="yesNo"
              v-model:selected="refineContractedOutModel"
              :width="25"
            />
          </div>
        </div>

        <div class="row mt-4">
          <div class="col">
            <a
              href=""
              class="btn btn-outline-dark text-start me-3"
              @click="onInitialEstBackClick($event)"
            >
              <i class="bi bi-arrow-left"></i>
              Back
            </a>
            <button
              type="button"
              class="btn btn-primary"
              @click="onInitialEstNextClick"
            >
              Next
            </button>
          </div>
        </div>
        <div v-if="showInitialEstError" class="row mt-3">
          <div class="col">
            <span class="warn"
              >Please tell us if you are interested in considering contracted
              out years further.</span
            >
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDefineCOPEEst" class="row">
      <div class="col-md-8 mx-auto">
        <p>
          You need to get what is called a "contracted out" adjustment from
          <a href="https://www.gov.uk/check-state-pension" target="_blank"
            >here</a
          >.
        </p>
        <p>
          Near the bottom of the page you may see the words "<i
            >You've been in a contracted-out pension scheme</i
          >". If not, there is no issue and you can ignore everything below.
        </p>
        <p>
          If so, click the link there and you will see. Your COPE estimate is
          &pound;C a week on the page.
        </p>
        <div class="row">
          <div class="col-5">
            <span class="me-2">Enter this C figure here:</span>
            <input
              type="text"
              v-model="estCOPEModel"
              size="3"
              maxlength="3"
              @keypress="checkNumeric"
            />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <a
              href=""
              class="btn btn-outline-dark text-start me-3"
              @click="onDefineCOPEEstBackClick($event)"
            >
              <i class="bi bi-arrow-left"></i>
              Back
            </a>
            <button
              type="button"
              class="btn btn-primary"
              @click="onDefineCOPEEstNextClick"
            >
              Next
            </button>
          </div>
        </div>
        <div v-if="showEstCopeError" class="row mt-3">
          <div class="col">
            <span class="warn">Please enter the COPE estimate.</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showUpdate" class="row">
      <div class="col-md-8 mx-auto">
        <template v-if="isIncludingCopeEst">
          <p>
            With this deduction, your revised estimated revalued State Pension
            from State Pension Age is now
            <span class="fw-bold">{{
              revisedStatePensionWithCOPEFormatted
            }}</span>
            per year.
          </p>
          <p>
            It may be the case that not all of this deduction will apply if you
            still accrue a full 35 years of NI contributions where no years were
            "contracted out", but if you want to be cautious you can add this
            full reduction.
          </p>
        </template>
        <p>
          Use the <span class="fst-italic">Update my Plan</span> button if you
          would like to update your State Pension information with the
          {{ updateEstimateText }} or use the
          <span class="fst-italic">Next</span> button if you would prefer to
          move on without updating.
        </p>

        <div class="row mt-2">
          <div class="col">
            <a
              href=""
              class="btn btn-outline-dark text-start me-3"
              @click="onUpdateBackClick($event)"
            >
              <i class="bi bi-arrow-left"></i>
              Back
            </a>
            <button
              type="button"
              class="btn btn-primary me-3"
              @click="onUpdateClick"
            >
              Update my Plan
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="onUpdateNextClick"
            >
              Next
            </button>
          </div>
        </div>
        <div v-if="showUpdateErrorMessage" class="row mt-3">
          <div class="col">
            <span class="warn">{{ updateErrorMessage }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showFinally" class="row">
      <div class="row">
        <div class="col-md-8 mx-auto">
          <template v-if="hasRefinedDetail">
            <p>
              Your plan has an estimated State Pension of
              {{ estimatedStatePension }}.
            </p>
          </template>
          <p>
            The State Pension changes each year depending on the future
            increases given and we will update this estimate each year
            automatically to allow for this.
          </p>
          <p>
            There are also a number of other issues which can affect your final
            State Pension to a lesser degree, but as you reach State Pension Age
            you can update the final figure if needed slightly once you know the
            exact amount.
          </p>
          <p>
            If you need more help on the State Pension, or want to buy more NI
            contribution years
            <a
              href="https://guiidewordpress.azurewebsites.net/dont-sleepwalk-into-a-low-state-pension/"
              target="_blank"
              >check out our blog post</a
            >
            which details everything you need to know.
          </p>

          <template v-if="hasRefinedDetail">
            <p>
              Use the reset button if you would like to restore the default
              estimate or work through the refinement process again.
            </p>
            <button
              type="button"
              class="btn btn-primary me-3"
              @click="onUndoRefineClick"
            >
              Reset
            </button>
            <p v-if="showResetErrorMessage" class="mt-3">
              <span class="warn">{{ resetErrorMessage }}</span>
            </p>
          </template>

          <div v-if="showFinallyBackButton" class="row mt-2">
            <div class="col">
              <a
                href=""
                class="btn btn-outline-dark text-start me-3"
                @click="onFinallyBackClick($event)"
              >
                <i class="bi bi-arrow-left"></i>
                Back
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { app } from "@/guiide/ts/app";
import { formatter } from "@/shared/ts/formatter";
import { StatePension } from "@/guiide/ts/statePension";
import { inputValidator } from "@/shared/ts/inputValidator";
import OptionsQuestion from "@/guiide/components/common/OptionsQuestion.vue";
import HeaderBanner from "./HeaderBanner.vue";

enum ActiveSection {
  OverSPA,
  UnderSPAIntro,
  InitialEst,
  DefineCOPEEst,
  Update,
  Finally
}

export default defineComponent({
  name: "StatePensionHelp",
  components: {
    OptionsQuestion,
    HeaderBanner
  },
  setup() {
    const yearsFullContribsModel = ref("");
    const yearsFutureContribsModel = ref("");
    const refineContractedOutModel = ref("");
    const estCOPEModel = ref("");

    return {
      yearsFullContribsModel,
      yearsFutureContribsModel,
      refineContractedOutModel,
      estCOPEModel
    };
  },
  data() {
    return {
      activeSection: ActiveSection.UnderSPAIntro,
      showUnderSPAError: false,
      showInitialEstError: false,
      updateErrorMessage: "",
      resetErrorMessage: "",
      yesNo: ["Yes", "No"],
      showEstCopeError: false,
      isIncludingCopeEst: false
    };
  },
  mounted() {
    const statePension: StatePension = this.$store.state.retiree?.statePension;

    if (statePension == null) {
      this.activeSection = ActiveSection.UnderSPAIntro;
      return;
    }

    if (statePension.hasRefinedDetail) {
      this.activeSection = ActiveSection.Finally;
      return;
    }

    this.activeSection =
      statePension?.isOverSPA ?? false
        ? ActiveSection.OverSPA
        : ActiveSection.UnderSPAIntro;
  },
  watch: {
    "$store.state.retiree"() {
      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;
      if (statePension != null && statePension.hasRefinedDetail) {
        this.activeSection = ActiveSection.Finally;
      }
    }
  },
  computed: {
    hasRefinedDetail: function(): boolean {
      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;
      return statePension?.hasRefinedDetail ?? false;
    },
    showOverSPA: function(): boolean {
      return this.activeSection == ActiveSection.OverSPA;
    },
    showUnderSPAIntro: function(): boolean {
      return this.activeSection == ActiveSection.UnderSPAIntro;
    },
    showInitialEst: function(): boolean {
      return this.activeSection == ActiveSection.InitialEst;
    },
    showDefineCOPEEst: function(): boolean {
      return this.activeSection == ActiveSection.DefineCOPEEst;
    },
    showUpdate: function(): boolean {
      return this.activeSection == ActiveSection.Update;
    },
    showFinally: function(): boolean {
      return this.activeSection == ActiveSection.Finally;
    },
    isOverSPA: function(): boolean {
      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;
      return statePension?.isOverSPA ?? false;
    },
    statePensionEntitlementDate: function(): string {
      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;
      return formatter.longDate(statePension?.entitlementDate);
    },
    estimatedStatePension: function(): string {
      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;
      if (statePension == null) {
        return "";
      }
      return formatter.formatCurrency(statePension.estimatedAmount);
    },
    contributionYearsDefined: function(): boolean {
      return !(
        this.yearsFullContribsModel == "" || this.yearsFutureContribsModel == ""
      );
    },
    totalContributionYears: function(): number {
      if (!this.contributionYearsDefined) {
        return 0;
      }
      return (
        parseInt(this.yearsFullContribsModel) +
        parseInt(this.yearsFutureContribsModel)
      );
    },
    hasQualifiedForPension: function(): boolean {
      return this.totalContributionYears >= 10;
    },
    revisedStatePension: function(): number {
      if (this.totalContributionYears < 10) {
        return 0;
      }

      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;

      if (statePension == null) {
        return 0;
      }

      if (this.totalContributionYears < 35) {
        return Math.round(
          statePension.estimatedAmount * (this.totalContributionYears / 35)
        );
      }

      return Math.round(statePension.estimatedAmount);
    },
    revisedStatePensionFormatted: function(): string {
      return formatter.formatCurrency(this.revisedStatePension);
    },
    entitlementAdvice: function(): string {
      if (this.totalContributionYears >= 35) {
        return "OK, it looks like you may get the full amount.";
      } else if (this.totalContributionYears >= 10) {
        return "You may want to see if you can buy additional NI contributions to get a higher State Pension.";
      } else {
        return "We strongly recommend that you see if you can buy enough additional NI contributions to at least get some State Pension.";
      }
    },
    estCOPEDefined: function(): boolean {
      return this.estCOPEModel != "";
    },
    revisedStatePensionWithCOPE: function(): number {
      if (!this.estCOPEDefined) {
        return 0;
      }

      const adjustmentFactor = this.estCOPEModel / 185.1;
      const revised = this.revisedStatePension * (1 - adjustmentFactor);
      return revised > 0 ? Math.round(revised) : 0;
    },
    revisedStatePensionWithCOPEFormatted: function(): string {
      return formatter.formatCurrency(this.revisedStatePensionWithCOPE);
    },
    updateEstimateText: function(): string {
      if (this.isIncludingCopeEst) {
        return "estimate";
      } else {
        return `estimated figure of ${this.revisedStatePensionFormatted}`;
      }
    },
    showUpdateErrorMessage: function(): boolean {
      return this.updateErrorMessage.length > 0;
    },
    showResetErrorMessage: function(): boolean {
      return this.resetErrorMessage.length > 0;
    },
    showFinallyBackButton: function(): boolean {
      return !this.$store.state.retiree?.statePension.hasRefinedDetail;
    }
  },
  methods: {
    reset() {
      const statePension: StatePension = this.$store.state.retiree
        ?.statePension;

      if (statePension == null) {
        this.activeSection = ActiveSection.UnderSPAIntro;
        return;
      }

      if (statePension.hasRefinedDetail) {
        this.activeSection = ActiveSection.Finally;
        return;
      }

      this.activeSection = this.isOverSPA
        ? ActiveSection.OverSPA
        : ActiveSection.UnderSPAIntro;
    },
    updateModelValues() {
      const setDefaults = this.$store.state.retiree == null;
      let age = 0;
      let allocation = 0;
      let notify = false;

      if (setDefaults) {
        age = this.minPurchaseAge;
        allocation = 0;
        notify = false;
      } else {
        if (this.$store.state.retiree.guaranteedIncome.subscribed) {
          age = this.$store.state.retiree.guaranteedIncome.ageAtSubscription;
          allocation = this.$store.state.retiree.guaranteedIncome.allocation;
        } else {
          age = this.minPurchaseAge;
          allocation = 0;
        }
        notify = this.$store.state.retiree.guaranteedIncome.notifyWhenAvailable;
      }

      this.ageModel = age;
      this.amountModel = allocation;
      this.notifyModel = notify;
    },
    checkNumeric(event) {
      inputValidator.checkNumeric(event);
    },
    onUnderSPAIntroNextClick() {
      if (!this.contributionYearsDefined) {
        this.showUnderSPAError = true;
        return;
      }

      this.showUnderSPAError = false;
      this.activeSection = ActiveSection.InitialEst;
    },
    onInitialEstBackClick(event) {
      event.preventDefault();
      this.showInitialEstError = false;
      this.activeSection = ActiveSection.UnderSPAIntro;
    },
    onInitialEstNextClick() {
      const hasAnsweredCOPEInterestQuestion =
        this.refineContractedOutModel != "";
      if (this.hasQualifiedForPension && !hasAnsweredCOPEInterestQuestion) {
        this.showInitialEstError = true;
        return;
      }

      this.showInitialEstError = false;
      const nextSection =
        this.refineContractedOutModel == "Yes"
          ? ActiveSection.DefineCOPEEst
          : ActiveSection.Update;
      this.isIncludingCopeEst = nextSection == ActiveSection.DefineCOPEEst;
      this.activeSection = nextSection;
    },
    onDefineCOPEEstBackClick(event) {
      event.preventDefault();
      this.activeSection = ActiveSection.InitialEst;
    },
    onDefineCOPEEstNextClick() {
      if (!this.estCOPEDefined) {
        this.showEstCopeError = true;
        return;
      }

      if (this.estCOPEModel == 0) {
        this.showEstCopeError = true;
        return;
      }

      this.showEstCopeError = false;
      this.activeSection = ActiveSection.Update;
    },
    onUpdateBackClick(event) {
      event.preventDefault();
      this.updateErrorMessage = "";
      const backToSection = this.isIncludingCopeEst
        ? ActiveSection.DefineCOPEEst
        : ActiveSection.InitialEst;
      this.activeSection = backToSection;
    },
    onUpdateNextClick() {
      this.updateErrorMessage = "";
      this.activeSection = ActiveSection.Finally;
    },
    async onUpdateClick() {
      this.updateErrorMessage = "";
      let amount = this.revisedStatePension;
      let copeDeduction = 0;

      if (this.isIncludingCopeEst) {
        amount = this.revisedStatePensionWithCOPE;
        copeDeduction = this.estCOPEModel;
      }
      const response = await app.refineStatePension(
        amount,
        this.yearsFullContribsModel,
        this.yearsFutureContribsModel,
        copeDeduction
      );

      if (!response.isOk) {
        this.updateErrorMessage = response.message;
        return;
      }

      this.activeSection = ActiveSection.Finally;
    },
    onFinallyBackClick(event) {
      event.preventDefault();
      this.activeSection = ActiveSection.Update;
    },
    async onUndoRefineClick() {
      this.resetErrorMessage = "";

      const response = await app.undoRefineStatePension();

      if (!response.isOk) {
        this.resetErrorMessage = response.message;
        return;
      }

      this.activeSection = this.isOverSPA
        ? ActiveSection.OverSPA
        : ActiveSection.UnderSPAIntro;
    }
  }
});
</script>

<style scoped>
.warn {
  color: #003d99;
}
</style>
